import { useTranslation } from "react-i18next";

import { LandingPageEventType } from "pages/Dashboard/types";
import AttributesCard from "pages/shared/Dashboard/AttributesCard";
import { LandingPageViewConfig } from "pages/shared/Dashboard/types";
import { getDefaultDetailsConfig } from "pages/shared/Dashboard/utils";

import { StatefulTabs, Tab } from "features/components/Tabs";
import { SchemaEntry } from "features/ui/MaterialTable";

interface EventDetailsTabbedProps {
  event: LandingPageEventType;
  config: LandingPageViewConfig;
  getSchemaForAccessor: (
    accessor: string,
    useShortLabel?: boolean
  ) => SchemaEntry<string> | undefined;
}

const EventDetailsTabbed = ({
  event,
  config,
  getSchemaForAccessor,
}: EventDetailsTabbedProps) => {
  const { t } = useTranslation();
  const detailsConfig =
    config.eventDetails ?? getDefaultDetailsConfig(config.eventType);
  const detailsNewConfig = config.eventDetailsNew;

  if (!detailsNewConfig) {
    return null;
  }

  // Build tabs based on configuration
  const tabs: Tab[] = detailsNewConfig.tabs.map((tab) => {
    const tabContent: JSX.Element[] = [];

    // Add card rows assigned to this tab
    detailsNewConfig.cardRowsConfig?.forEach((rowConfig, rowIndex) => {
      if (rowConfig.tabId === tab.id) {
        const cardRow = detailsConfig.cardRows[rowIndex];
        if (cardRow) {
          tabContent.push(
            <div
              key={`card-row-${rowIndex}`}
              className="flex flex-row gap-4 p-4 px-8 pb-0"
            >
              {cardRow.map((card, cardIndex) => {
                const cardNewConfig = rowConfig.cards[cardIndex];

                return (
                  <div
                    className={cardNewConfig?.containerClass || "flex-1"}
                    key={cardIndex}
                  >
                    <AttributesCard
                      item={event}
                      attributes={card.fields}
                      title={card.title}
                      direction={cardNewConfig?.type || card.type}
                      getSchemaForAccessor={getSchemaForAccessor}
                      mainFields={detailsConfig.mainFields}
                      mainFieldsDirection={
                        detailsConfig.mainFieldsDirection || "vertical"
                      }
                      useNewConfig={detailsNewConfig.useNewConfig}
                    />
                  </div>
                );
              })}
            </div>
          );
        }
      }
    });

    return {
      key: tab.id,
      title: tab.name,
      content: (
        <div className="bg-white">
          {tabContent.length > 0 ? (
            tabContent
          ) : (
            <div className="p-4 px-8 text-gray-400">
              {t("schema:dashboard.no_content_assigned")}
            </div>
          )}
        </div>
      ),
    };
  });

  return (
    <div className="bg-white">
      <StatefulTabs
        tabs={tabs}
        disableUsingQuery
        persistLocalStorageKey={`event-details-${config.eventType}`}
      />
    </div>
  );
};

export default EventDetailsTabbed;
